/**
 * useFiltro Composable
 *
 * A Vue 3 composable that provides filter functionality for franchises, employees, and date ranges.
 * This composable handles all the business logic for filtering data across the application.
 *
 * Features:
 * - Franchise and employee selection
 * - Date range filtering
 * - Server synchronization
 * - State management
 * - Event handling
 *
 * ============================================================================
 * TYPED DATA LAYER: ISO 8601 DATE FORMAT
 * ============================================================================
 *
 * Date formats in this composable:
 * - API communication: ISO 8601 (YYYY-MM-DD) for filter-service
 * - Internal state: ISO 8601 strings (DataInicio, DataFim)
 * - DatePicker/UI: Date objects (use conversion utilities below)
 * - Display: Locale-aware format via i18n (e.g., DD/MM/YYYY for pt-BR, MM/DD/YYYY for en-US)
 *
 * Conversion utilities provided:
 * - filterDateToDateObject(YYYY-MM-DD) → Date (for DatePicker)
 * - dateObjectToFilterDate(Date) → YYYY-MM-DD (for API)
 * - formatDateForDisplay(YYYY-MM-DD) → locale-aware string (for user display)
 *
 * For entity datetime fields (with time component), use useDateFormatter:
 * - fromApiDateTimeFormat("...Z") → Date object
 * - toApiDateTimeFormat(Date) → "...Z"
 *
 * ============================================================================
 *
 * Usage:
 * ```javascript
 * const {
 *   // State
 *   filterData,
 *   selectedFranchises,
 *   loading,
 *
 *   // Actions
 *   init,
 *   selectAllFranchises,
 *   toggleFranchise,
 *   applyFilter,
 *
 *   // Date Utilities
 *   filterDateToDateObject,
 *   dateObjectToFilterDate,
 *   formatDateForDisplay
 * } = useFiltro()
 * ```
 */

import { reactive, computed, ref, inject } from 'vue';
import { useDateFormatter } from './useDateFormatter';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';
import { httpService } from '../services/httpService';
import { permissionsState } from '../services/permissionsService';
import { cacheService } from '../services/cache/index';

import '../contracts/Filtro_AplicafiltrosResponse';
import '../contracts/Filtro_AplicaFiltrosInput';
import '../contracts/Filtro_PegafiltrosResponse';
import type { Filtro_AplicaFiltrosInput } from '../contracts/Filtro_AplicaFiltrosInput';
// Cache key for filter data
const FILTER_CACHE_KEY = 'userFilter';



// Global reactive state (shared across all instances)
const filtroState = reactive({
  filterData: {
    Franquias: {} as Record<string, any>,
    DataInicio: '',
    DataFim: ''
  },
  selectedFranchises: [] as Array<{ id: string; name: string }>,
  selectedPeriod: 'Não definido',
  showDateAlert: false,
  loading: true,
  initialized: false
});

// Global local state (shared across all instances)
const localState = reactive({
  searchTerm: '',
  currentFranchiseId: null,
  currentFranchiseName: '',
  currentFranchiseEmployees: [],
  dateRangeValue: '',
  initializing: false
});

// Global filtered franchises ref (shared across all instances)
const filteredFranchises = ref<any[]>([]);

/**
 * Compare two filter data objects
 * Returns true if they are different
 */
function compareFilterData(oldData: any, newData: any): boolean {
  try {
    // Quick reference check
    if (oldData === newData) return false;

    // Deep comparison of the objects
    const oldStr = JSON.stringify(oldData);
    const newStr = JSON.stringify(newData);
    const isDifferent = oldStr !== newStr;

    if (isDifferent) {
      log('useFiltro', 'compareFilterData', 'Filter data changed');
    }

    return isDifferent;
  } catch (error) {
    log('useFiltro', 'compareFilterData', 'Error comparing filter data', error, 'error');
    return true; // Assume different on error
  }
}

/**
 * useFiltro Composable
 * @param {Object} options - Configuration options
 * @returns {Object} Filtro state and methods
 */
export function useFiltro(options: any = {}) {
  // Get i18n composable
  const { t } = useI18n();

  // Get locale-aware date formatting from useDateFormatter
  const { formatDate, fromApiDateToDate } = useDateFormatter();

  // Use permissionsState directly instead of injection to avoid initialization timing issues
  const getPermissions = () => {
    if (!permissionsState.permissionsLoaded) {
      log('useFiltro', 'getPermissions', 'Permissions not loaded yet');
      return null;
    }
    return {
      user: permissionsState.user,
      franquias: permissionsState.franquias,
      locale: permissionsState.normalizedLocale,
      permissionsLoaded: permissionsState.permissionsLoaded
    };
  };

  // Use global state (no local state creation)

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================

  const filterData = computed(() => filtroState.filterData);
  const selectedFranchises = computed(() => filtroState.selectedFranchises);
  const showDateAlert = computed(() => filtroState.showDateAlert);
  const loading = computed(() => filtroState.loading);
  const initialized = computed(() => filtroState.initialized);
  const selectedFranchisesCount = computed(() => filtroState.selectedFranchises.length);

  const selectedPeriod = computed(() => {
    if (filtroState.filterData.DataInicio && filtroState.filterData.DataFim) {
      // Display dates in user-friendly DD/MM/YYYY format
      const startDisplay = formatDateForDisplay(filtroState.filterData.DataInicio);
      const endDisplay = formatDateForDisplay(filtroState.filterData.DataFim);
      return `${startDisplay} - ${endDisplay}`;
    }
    return t('not_defined') || 'Não definido';
  });

  const allFranchisesSelected = computed(() => {
    return filteredFranchises.value.length > 0 &&
      filtroState.selectedFranchises.length === filteredFranchises.value.length;
  });

  // ========================================
  // CORE BUSINESS LOGIC
  // ========================================

  /**
   * Initialize the filter system
   * Uses CacheService for caching (60 min TTL, 30 min check interval)
   */
  const init = async () => {
    // Prevent multiple initializations
    if (filtroState.initialized || localState.initializing) {
      log('useFiltro', 'init', 'Already initialized or initializing, skipping');
      return;
    }

    try {
      localState.initializing = true;
      log('useFiltro', 'init', 'Starting filter initialization...');

      // Sem cache local: o filtro é DURÁVEL no backend desde 2026-09-01
      // (write-through em `filtros_usuarios` + reidratação no miss do
      // memcached), então todo full load lê a verdade do servidor. A cópia em
      // IndexedDB existia para dar durabilidade quando o backend só tinha o
      // memcached — e produzia a divergência "UI mostra 2, backend serve 297".
      // Plano: docs/plans/auth-state-jwt-like-sem-cache-local-plan.md
      const data = await httpService.get('/index.php/filtro/pegaFiltros/', { responseContract: 'Filtro_PegafiltrosResponse' });
      const filterData = data?.dados ?? null;
      if (!filterData) {
        log('useFiltro', 'init', 'No filter data in API response, using defaults');
      }

      if (filterData) {
        log('useFiltro', 'init', 'Filter data loaded', { hasData: true });
        filtroState.filterData = filterData;
        await updateSelectedFranchises();
        updateDateRangeInput();
      } else {
        log('useFiltro', 'init', 'No filter data available, using defaults');
      }

      await updateFilteredFranchises();

      filtroState.loading = false;
      filtroState.initialized = true;
      localState.initializing = false;

      // Emit filter.updated for initial load
      emitFilterUpdated();

      log('useFiltro', 'init', 'Filter initialization completed successfully');
    } catch (error) {
      localState.initializing = false;
      console.error('Error initializing filter:', error);
      log('useFiltro', 'init', 'Error during initialization', error);

      // Set as initialized with default state to prevent infinite loading
      filtroState.loading = false;
      filtroState.initialized = true;
    }
  };

  /**
   * Load user filter from server (forces refresh, bypassing cache)
   * Compares with existing data and only emits update if changed
   */
  const loadUserFilter = async () => {
    try {
      log('useFiltro', 'loadUserFilter', 'Force loading user filter from API...');

      // Invalidate cache to force fresh fetch
      await cacheService.invalidate(FILTER_CACHE_KEY);

      // Fetch fresh data
      const data = await httpService.get('/index.php/filtro/pegaFiltros/', { responseContract: 'Filtro_PegafiltrosResponse' });

      log('useFiltro', 'loadUserFilter', 'API response received:', data);

      // Handle both "Dados" (capital) and "dados" (lowercase) for backward compatibility
      const filterData = data?.dados;

      if (filterData) {
        const oldFilter = { ...filtroState.filterData };
        const newFilter = filterData;

        // Update reactive state with filter data from API response
        filtroState.filterData = newFilter;
        log('useFiltro', 'loadUserFilter', 'Updated filtroState.filterData:', filtroState.filterData);

        // Store in cache
        // Update UI state
        await updateSelectedFranchises();
        updateDateRangeInput();

        log('useFiltro', 'loadUserFilter', 'User filter loaded successfully');

        // Return whether data changed (for caller to decide if emit is needed)
        return compareFilterData(oldFilter, newFilter);
      } else {
        log('useFiltro', 'loadUserFilter', 'No filter data in API response, using defaults');
        return false;
      }
    } catch (error) {
      log('useFiltro', 'loadUserFilter', 'Error loading user filter', error);
      throw error;
    }
  };

  /**
   * Update selected franchises from filter data
   */
  const updateSelectedFranchises = async () => {
    filtroState.selectedFranchises = [];

    const availableFranchises = await getAvailableFranchises();

    for (const franchiseId in filtroState.filterData.Franquias) {
      const franchise = availableFranchises.find(f => f.id === franchiseId);
      if (franchise) {
        filtroState.selectedFranchises.push({
          id: franchiseId,
          name: franchise.name
        });
      }
    }

    // Update filtered franchises after loading available franchises
    await updateFilteredFranchises();
  };

  /**
   * Update date range input
   */
  const updateDateRangeInput = () => {
    if (filtroState.filterData.DataInicio && filtroState.filterData.DataFim) {
      localState.dateRangeValue = `${filtroState.filterData.DataInicio} - ${filtroState.filterData.DataFim}`;
      // Update selectedPeriod in state
      filtroState.selectedPeriod = selectedPeriod.value;
    }
  };

  // ========================================
  // FRANCHISE METHODS
  // ========================================

  /**
   * Get available franchises from permissions
   */
  const getAvailableFranchises = async () => {
    try {
      log('useFiltro', 'getAvailableFranchises', 'Getting available franchises...');
      const permissions = getPermissions();
      log('useFiltro', 'getAvailableFranchises', 'permissions object exists:', !!permissions);

      const franquiasObject = permissions?.franquias;
      log('useFiltro', 'getAvailableFranchises', 'franquias object exists:', !!franquiasObject);
      log('useFiltro', 'getAvailableFranchises', 'franquias keys count:', franquiasObject ? Object.keys(franquiasObject).length : 0);

      // Convert franchises object to array format for compatibility with .find()
      const franquiasArray = [];
      for (const franchiseId in franquiasObject) {
        if (franquiasObject[franchiseId] && franquiasObject[franchiseId].Dados) {
          franquiasArray.push({
            id: franchiseId,
            name: franquiasObject[franchiseId].Dados.Nome || '',
            data: franquiasObject[franchiseId],
            employees: franquiasObject[franchiseId].Funcionarios || {}
          });
        }
      }

      log('useFiltro', 'getAvailableFranchises', 'Converted franchises array length:', franquiasArray.length);
      return franquiasArray;
    } catch (error) {
      log('useFiltro', 'getAvailableFranchises', 'Error converting franchises', error);
      return [];
    }
  };

  /**
   * Update filtered franchises based on search term
   */
  const updateFilteredFranchises = async () => {
    try {
      log('useFiltro', 'updateFilteredFranchises', 'Starting to update filtered franchises...');
      // Always get available franchises from the event system
      const availableFranchises = await getAvailableFranchises();
      log('useFiltro', 'updateFilteredFranchises', 'Available franchises count:', availableFranchises.length);

      // Filter based on search term
      if (!localState.searchTerm || localState.searchTerm.trim() === '') {
        filteredFranchises.value = [...availableFranchises];
        log('useFiltro', 'updateFilteredFranchises', 'No search term, showing all franchises:', filteredFranchises.value.length);
      } else {
        const searchTermValue = localState.searchTerm.toLowerCase().trim();
        filteredFranchises.value = availableFranchises.filter(franchise =>
          franchise.name.toLowerCase().includes(searchTermValue)
        );
        log('useFiltro', 'updateFilteredFranchises', 'Filtered franchises for term "' + searchTermValue + '":', filteredFranchises.value.length);
      }
    } catch (error) {
      console.error('Error updating filtered franchises:', error);
      log('useFiltro', 'updateFilteredFranchises', 'Error updating filtered franchises:', error);
      filteredFranchises.value = [];
    }
  };

  /**
   * Select or deselect all franchises
   */
  const selectAllFranchises = async () => {
    // Always get available franchises from the event system
    const availableFranchises = await getAvailableFranchises();

    if (filtroState.selectedFranchises.length > 0 &&
      filtroState.selectedFranchises.length === availableFranchises.length) {
      // Deselect all franchises and employees
      filtroState.selectedFranchises = [];
      filtroState.filterData.Franquias = {};
    } else {
      // Select all franchises and all their employees
      filtroState.selectedFranchises = availableFranchises.map(f => ({
        id: f.id,
        name: f.name
      }));

      // Auto-select all employees for all franchises
      availableFranchises.forEach(franchise => {
        const employeeIds = franchise.employees ? Object.keys(franchise.employees) : [];
        (filtroState.filterData.Franquias as any)[franchise.id] = {
          FranquiaID: franchise.id,
          FuncionariosIDs: employeeIds
        };
      });
    }
  };

  /**
   * Toggle franchise selection
   */
  const toggleFranchise = (franchise: any) => {
    const index = filtroState.selectedFranchises.findIndex(f => f.id === franchise.id);

    if (index > -1) {
      // Remove franchise and all its employees
      filtroState.selectedFranchises.splice(index, 1);
      delete filtroState.filterData.Franquias[franchise.id];
    } else {
      // Add franchise and all its employees
      filtroState.selectedFranchises.push({
        id: franchise.id,
        name: franchise.name
      });

      // Auto-select all employees for this franchise
      const employeeIds = franchise.employees ? Object.keys(franchise.employees) : [];
      (filtroState.filterData.Franquias as any)[franchise.id] = {
        FranquiaID: franchise.id,
        FuncionariosIDs: employeeIds
      };
    }
  };

  /**
   * Check if franchise is selected
   */
  const isFranchiseSelected = (franchiseId: number) => {
    return filtroState.selectedFranchises.some(f => f.id === String(franchiseId));
  };

  // ========================================
  // EMPLOYEE METHODS
  // ========================================

  /**
   * Set current franchise for employee management
   */
  const setCurrentFranchise = (franchise: any) => {
    localState.currentFranchiseId = franchise.id;
    localState.currentFranchiseName = franchise.name;

    // Get employees from the franchise object
    const employees = franchise.employees || {};
    localState.currentFranchiseEmployees = Object.values(employees);
  };

  /**
   * Toggle employee selection
   */
  const toggleEmployee = (employee: any) => {
    const franchiseId = localState.currentFranchiseId;

    // Ensure franchise exists in filter data
    if (franchiseId && !(filtroState.filterData.Franquias as any)[franchiseId]) {
      (filtroState.filterData.Franquias as any)[franchiseId] = {
        FranquiaID: franchiseId,
        FuncionariosIDs: []
      };
    }

    const employeeIds = (franchiseId && (filtroState.filterData.Franquias as any)[franchiseId]?.FuncionariosIDs) || [];
    const employeeId = employee.funcionario_id;

    const index = (employeeIds as any).indexOf(employeeId);
    if (index > -1) {
      // Remove employee
      (employeeIds as any).splice(index, 1);
    } else {
      // Add employee
      (employeeIds as any).push(employeeId);
    }

    // Update the filter data
    if (franchiseId && (filtroState.filterData.Franquias as any)[franchiseId]) {
      (filtroState.filterData.Franquias as any)[franchiseId].FuncionariosIDs = employeeIds;
    }
  };

  /**
   * Check if employee is selected
   */
  const isEmployeeSelected = (employeeId: number) => {
    // Check if employee is selected in current franchise
    const franchiseId = localState.currentFranchiseId;
    if (!franchiseId) return false;
    const franquiaData = (filtroState.filterData.Franquias as any)[franchiseId];
    return franquiaData &&
      franquiaData.FuncionariosIDs &&
      franquiaData.FuncionariosIDs.includes(employeeId);
  };

  // ========================================
  // DATE RANGE METHODS
  // ========================================

  /**
   * Update date range value
   */
  const updateDateRange = (value: any) => {
    localState.dateRangeValue = value;
  };

  /**
   * Get date range from input
   * TYPED DATA LAYER: Returns dates in ISO 8601 format (YYYY-MM-DD) for API
   */
  const getDateRange = () => {
    // TYPED DATA LAYER: Helper function to format any date to ISO 8601 (YYYY-MM-DD)
    const ensureISO8601 = (dateValue: any) => {
      if (!dateValue) return '';

      // If it's already a string in ISO 8601 format (YYYY-MM-DD), return it
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }

      // If it's a Date object or date string, convert it
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${year}-${month}-${day}`;
      } catch (error) {
        log('useFiltro', 'getDateRange', 'Error formatting date', { dateValue, error });
        return '';
      }
    };

    log('useFiltro', 'getDateRange', 'Getting date range', {
      dateRangeValue: localState.dateRangeValue,
      filterDataInicio: filtroState.filterData.DataInicio,
      filterDataFim: filtroState.filterData.DataFim
    });

    // If dateRangeValue is not set, use the values from filterData
    if (!localState.dateRangeValue || localState.dateRangeValue === '') {
      const result = {
        startDate: ensureISO8601(filtroState.filterData.DataInicio),
        endDate: ensureISO8601(filtroState.filterData.DataFim)
      };

      log('useFiltro', 'getDateRange', 'Using filterData dates', result);
      return result;
    }

    const dateRangeValueStr = String(localState.dateRangeValue);
    const dates = dateRangeValueStr.split(' - ');

    let startDate = dates[0] || '';
    let endDate = dates[1] || '';

    log('useFiltro', 'getDateRange', 'Parsing dateRangeValue', {
      dateRangeValueStr,
      startDate,
      endDate
    });

    // TYPED DATA LAYER: Ensure dates are in ISO 8601 format (YYYY-MM-DD)
    startDate = ensureISO8601(startDate);
    endDate = ensureISO8601(endDate);

    const result = { startDate, endDate };
    log('useFiltro', 'getDateRange', 'Final result', result);
    return result;
  };

  /**
   * Check if date alert should be shown
   */
  const checkDateAlert = () => {
    if (filtroState.filterData.DataInicio && filtroState.filterData.DataFim) {
      log('useFiltro', 'checkDateAlert', 'Checking date alert', {
        DataInicio: filtroState.filterData.DataInicio,
        DataFim: filtroState.filterData.DataFim
      });

      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day
        
        // Parse DD/MM/YYYY format
        const [startDay, startMonth, startYear] = filtroState.filterData.DataInicio.split('/');
        const startDate = new Date(Number(startYear), Number(startMonth) - 1, Number(startDay));
        startDate.setHours(0, 0, 0, 0);

        const [endDay, endMonth, endYear] = filtroState.filterData.DataFim.split('/');
        const endDate = new Date(Number(endYear), Number(endMonth) - 1, Number(endDay));
        endDate.setHours(23, 59, 59, 999); // End of the end date
        
        // Check if today is outside the selected period
        filtroState.showDateAlert = today > endDate || today < startDate;
        
        log('useFiltro', 'checkDateAlert', 'Date alert check completed', {
          showDateAlert: filtroState.showDateAlert,
          today: today.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      } catch (error) {
        log('useFiltro', 'checkDateAlert', 'Error checking date alert', error);
        filtroState.showDateAlert = false;
      }
    } else {
      filtroState.showDateAlert = false;
    }
  };

  // ========================================
  // FILTER APPLICATION
  // ========================================

  /**
   * Apply the current filter
   */
  const applyFilter = async () => {
    try {
      const filterData = await processFilterData();
      await sendFilterToServer(filterData);
      await saveFilterData(filterData);

      // Save to cache (IndexedDB) with fresh timestamp
      // Use JSON roundtrip to strip Vue reactive proxies (IndexedDB can't clone them)
      // Always emit when user manually applies filter
      emitFilterUpdated();

      return { success: true };
    } catch (error) {
      console.error('Error applying filter:', error);
      throw error;
    }
  };

  /**
   * Process filter data for submission
   */
  const processFilterData = async () => {
    const dateRange = getDateRange();
    const franchises = getSelectedFranchises();

    return {
      Franquias: franchises,
      DataInicio: dateRange.startDate,
      DataFim: dateRange.endDate
    };
  };

  /**
   * Get selected franchises data
   */
  const getSelectedFranchises = () => {
    const franchises: any = {};

    filtroState.selectedFranchises.forEach(franchise => {
      const franchiseId = franchise.id;

      // Use the filterData which contains the actual selected employees
      if ((filtroState.filterData.Franquias as any)[franchiseId]) {
        franchises[franchiseId] = {
          FranquiaID: parseInt(franchiseId), // Ensure it's a number
          FuncionariosIDs: (filtroState.filterData.Franquias as any)[franchiseId].FuncionariosIDs || []
        };
      }
    });

    return franchises;
  };

  /**
   * Send filter to server
   */
  const sendFilterToServer = async (filterData: Filtro_AplicaFiltrosInput) => {
    const data = await httpService.post('/index.php/filtro/aplicaFiltros/', filterData, {
      logContext: 'useFiltro.sendFilterToServer',
      responseContract: 'Filtro_AplicafiltrosResponse',
      inputContract: 'Filtro_AplicaFiltrosInput'
    });

    if (!data?.success) {
      const erro = data?.error || 'Failed to apply filter';
      throw new Error(erro);
    }

    return data?.dados;
  };

  /**
   * Save filter data to server
   */
  const saveFilterData = async (filterData: any) => {
    try {
      // The filter is already saved when we call aplicaFiltros, 
      // but we can update our reactive state to match what was saved
      filtroState.filterData = {
        Franquias: filterData.Franquias,
        DataInicio: filterData.DataInicio,
        DataFim: filterData.DataFim
      };
      // Update selectedPeriod
      filtroState.selectedPeriod = selectedPeriod.value;
    } catch (error) {
      console.error('Error saving filter data:', error);
      throw error;
    }
  };

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Format ISO 8601 date (YYYY-MM-DD) to locale-aware display format
   * TYPED DATA LAYER: Use this ONLY for user-facing display, not for API calls
   * Uses i18n locale settings for proper formatting (e.g., DD/MM/YYYY for pt-BR, MM/DD/YYYY for en-US)
   *
   * @param {string} dateStr - Date in ISO 8601 format (YYYY-MM-DD)
   * @returns {string} - Date formatted according to user's locale
   */
  const formatDateForDisplay = (dateStr: any) => {
    if (!dateStr || typeof dateStr !== 'string') return '';
    // TYPED DATA LAYER: Filter dates are date-only (no timezone).
    // Use fromApiDateToDate() which creates at noon local time to avoid timezone edge cases.
    const dateObj = fromApiDateToDate(dateStr);
    if (!dateObj) return dateStr;
    return formatDate(dateObj);
  };

  // Legacy alias for backward compatibility
  const formatDateToBR = formatDateForDisplay;

  /**
   * Convert filter date (ISO 8601: YYYY-MM-DD) to Date object for DatePicker
   * TYPED DATA LAYER: API uses ISO 8601, DatePicker needs Date objects
   *
   * @param {string} dateStr - Date in ISO 8601 format (YYYY-MM-DD)
   * @returns {Date|null} - Date object or null if invalid
   */
  const filterDateToDateObject = (dateStr: any) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    const [year, month, day] = parts.map(Number);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    // Create date at noon to avoid timezone edge cases
    return new Date(year, month - 1, day, 12, 0, 0);
  };

  /**
   * Convert Date object to filter date (ISO 8601: YYYY-MM-DD) for API
   * TYPED DATA LAYER: DatePicker returns Date objects, API needs ISO 8601
   *
   * @param {Date} date - Date object from DatePicker
   * @returns {string|null} - Date in ISO 8601 format (YYYY-MM-DD) or null if invalid
   */
  const dateObjectToFilterDate = (date: any) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  /**
   * Get filter date range as Date objects for DatePicker
   * TYPED DATA LAYER: Convenience method for components using DatePicker
   *
   * @returns {{ startDate: Date|null, endDate: Date|null }}
   */
  const getDateRangeAsDateObjects = () => {
    return {
      startDate: filterDateToDateObject(filtroState.filterData.DataInicio),
      endDate: filterDateToDateObject(filtroState.filterData.DataFim)
    };
  };

  // ========================================
  // EVENT HANDLING
  // ========================================

  /**
   * Emit filter updated event
   */
  const emitFilterUpdated = () => {
    checkDateAlert();

    // Emit global event for other components
    eventBus.emit('filter.updated', {
      filterData: filtroState.filterData,
      selectedFranchises: filtroState.selectedFranchises,
      selectedPeriod: filtroState.selectedPeriod,
      showDateAlert: filtroState.showDateAlert
    });

    log('useFiltro', 'emitFilterUpdated', 'Filter updated event emitted');
  };

  /**
   * Refresh filter from API in background
   * Only emits update if data changed
   */
  const refreshFilterFromAPI = async () => {
    try {
      log('useFiltro', 'refreshFilterFromAPI', 'Starting background refresh...');
      const dataChanged = await loadUserFilter();

      if (dataChanged) {
        log('useFiltro', 'refreshFilterFromAPI', 'Filter data changed, emitting update');
        emitFilterUpdated();
      } else {
        log('useFiltro', 'refreshFilterFromAPI', 'No changes detected');
      }
    } catch (error) {
      log('useFiltro', 'refreshFilterFromAPI', 'Error refreshing filter', error, 'error');
      // Silent fail - don't disrupt user experience
    }
  };

  /**
   * Start auto-refresh timer (now handled by CacheService)
   * @deprecated CacheService handles auto-refresh automatically
   */
  const startAutoRefresh = () => {
    log('useFiltro', 'startAutoRefresh', 'Auto-refresh now handled by CacheService');
    // CacheService handles auto-refresh based on cacheConfig.js settings
    // (60 min TTL, 30 min check interval for userFilter)
  };

  /**
   * Stop auto-refresh timer (now handled by CacheService)
   * @deprecated CacheService handles auto-refresh automatically
   */
  const stopAutoRefresh = () => {
    log('useFiltro', 'stopAutoRefresh', 'Auto-refresh now handled by CacheService');
    // CacheService handles cleanup automatically
  };

  // ========================================
  // SERVICE METHODS
  // ========================================

  /**
   * Get selected franchises formatted as select array
   */
  const getSelectedFranchisesSelectArray = async () => {
    const franchises = {};

    // Get permissions data
    const permissions = getPermissions();

    // Guard clause - return empty array if no permissions
    if (!permissions?.franquias) {
      log('useFiltro', 'getSelectedFranchisesSelectArray', 'No permissions.franquias available');
      return [];
    }

    // Get available franchises from permissions
    const availableFranchises = Object.keys(permissions.franquias).map(id => ({
      id: id,
      name: permissions.franquias[id].Dados.Nome
    }));

    for (const franchiseId in filtroState.filterData.Franquias) {
      const franchise = availableFranchises.find(f => f.id === franchiseId);
      (franchises as any)[franchiseId] = {
        FranquiaID: franchiseId,
        FranquiaNome: franchise ? franchise.name : ''
      };
    }

    const output: any[] = [];
    for (const id in franchises) {
      output.push({
        Name: (franchises as any)[id].FranquiaNome,
        ID: (franchises as any)[id].FranquiaID
      });
    }

    log('useFiltro', 'getSelectedFranchisesSelectArray', 'Returning franchises', output.length);
    return output;
  };

  /**
   * Get selected franchises as array (for backward compatibility)
   */
  const getSelectedFranchisesArray = async () => {
    const franchises: any = {};
    const availableFranchises = await getAvailableFranchises();

    for (const franchiseId in filtroState.filterData.Franquias) {
      const franchise = availableFranchises.find(f => f.id === franchiseId);
      (franchises as any)[franchiseId] = {
        FranquiaID: franchiseId,
        FranquiaNome: franchise ? franchise.name : ''
      };
    }
    return franchises;
  };

  // ========================================
  // SEARCH FUNCTIONALITY
  // ========================================

  /**
   * Set search term and update filtered franchises
   */
  const setSearchTerm = async (term: any) => {
    localState.searchTerm = term;
    await updateFilteredFranchises();
  };

  // ========================================
  // RETURN API
  // ========================================

  return {
    // Global State (reactive)
    filterData,
    selectedFranchises,
    showDateAlert,
    loading,
    initialized,
    selectedFranchisesCount,
    selectedPeriod,
    allFranchisesSelected,

    // Local State (reactive)
    searchTerm: computed(() => localState.searchTerm),
    currentFranchiseId: computed(() => localState.currentFranchiseId),
    currentFranchiseName: computed(() => localState.currentFranchiseName),
    currentFranchiseEmployees: computed(() => localState.currentFranchiseEmployees),
    dateRangeValue: computed(() => localState.dateRangeValue),
    initializing: computed(() => localState.initializing),
    filteredFranchises,

    // Core Methods
    init,
    loadUserFilter,

    // Franchise Methods
    getAvailableFranchises,
    updateFilteredFranchises,
    selectAllFranchises,
    toggleFranchise,
    isFranchiseSelected,

    // Employee Methods
    setCurrentFranchise,
    toggleEmployee,
    isEmployeeSelected,

    // Date Range Methods
    updateDateRange,
    getDateRange,
    checkDateAlert,

    // Filter Application
    applyFilter,
    processFilterData,
    getSelectedFranchises,

    // Utility Methods (TYPED DATA LAYER)
    formatDateForDisplay, // YYYY-MM-DD → DD/MM/YYYY (for display only)
    formatDateToBR,       // Legacy alias for formatDateForDisplay
    filterDateToDateObject, // YYYY-MM-DD → Date object
    dateObjectToFilterDate, // Date object → YYYY-MM-DD
    getDateRangeAsDateObjects,
    emitFilterUpdated,
    refreshFilterFromAPI,
    startAutoRefresh,
    stopAutoRefresh,

    // Service Methods
    getSelectedFranchisesSelectArray,
    getSelectedFranchisesArray,

    // Search
    setSearchTerm,

    // Raw state access (for advanced use cases)
    $state: filtroState,
    $localState: localState
  };
}

// Export the reactive state for direct access if needed
export { filtroState };

// Cleanup on window unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // refreshInterval is defined inside useFiltro() scope
    // No global cleanup needed here
  });
}
